import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { Heart, Clock, Zap } from "lucide-react";

const ProductCard = ({ deal, index = 0, isAuthenticated = false }) => {
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.auth);
  const isActive = user?.is_active === 1 || user?.is_active === true;
  const {
    id,
    title,
    image,
    originalPrice,
    price,
    discounted_price,
    deal_slug,
    company_name,
    offer_type,
    bundle,
    bogo,
    discount_percentage,
  } = deal;
  
  const [countdown, setCountdown] = useState(null);
  const [daysRemaining, setDaysRemaining] = useState(null);
  const [isExpired, setIsExpired] = useState(false);
  
  // Check if deal is a flash sale
  // If validity_type is null or undefined, it's treated as a regular deal
  const isFlashSale = deal?.validity_type === "flash_sale";
  
  // Flash sale countdown timer and regular deal countdown
  useEffect(() => {
    // Calculate days remaining for regular deals (when validity_type is null or not flash_sale)
    const calculateDaysRemaining = () => {
      const expiryDate = deal?.expiry_date;
      
      if (!expiryDate) return null;
      
      const expiry = new Date(expiryDate);
      const today = new Date();
      
      // Validate date
      if (isNaN(expiry.getTime())) {
        return null;
      }
      
      // Reset time part for accurate day calculation
      expiry.setHours(0, 0, 0, 0);
      today.setHours(0, 0, 0, 0);
      
      const diffTime = expiry - today;
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      
      return diffDays;
    };

    // Handle flash sales
    if (isFlashSale && deal?.flash && Array.isArray(deal.flash) && deal.flash.length > 0) {
      const flashData = deal.flash[0];
      const endTime = new Date(flashData.flash_end_time);
      
      const updateCountdown = () => {
        const now = new Date();
        const difference = endTime - now;
        
        if (difference <= 0) {
          setCountdown({ hours: 0, minutes: 0, seconds: 0, expired: true });
          setIsExpired(true);
          return;
        }
        
        const hours = Math.floor(difference / (1000 * 60 * 60));
        const minutes = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60));
        const seconds = Math.floor((difference % (1000 * 60)) / 1000);
        
        setCountdown({ hours, minutes, seconds, expired: false });
        setIsExpired(false);
      };
      
      // Initial update
      updateCountdown();
      
      // Update every second
      const interval = setInterval(updateCountdown, 1000);
      
      return () => clearInterval(interval);
    } else {
      // For regular deals (validity_type is null or not "flash_sale"), calculate days remaining
      const days = calculateDaysRemaining();
      setDaysRemaining(days);
      setIsExpired(days !== null && days <= 0);
    }
  }, [deal?.flash, isFlashSale, deal?.expiry_date]);


  const discountPercentage =
    (originalPrice || price) && discounted_price
      ? Math.round((((originalPrice || price) - discounted_price) / (originalPrice || price)) * 100)
      : 0;

  const handlePromote = (deal) => {
    if (!isAuthenticated) {
      navigate("/login");
    } else {
      navigate(`/deal-authenticated/${deal_slug || id}`, { state: { dealData: deal } });
    }
  };

  const handleViewDeal = (deal) => {
    if (!isAuthenticated) {
      navigate(`/deal/${deal_slug}`);
    } else {
      navigate(`/deal-authenticated/${deal_slug}`);
    }
  };

  // Render pricing based on offer type
  const renderPricing = () => {
    // BOGO Offer
    if (offer_type === "bogo" && bogo && bogo.length > 0 && bogo[0].bogo_item_price) {
      const bogoData = bogo[0];
      return (
        <div className="space-y-1">
          <div className="flex gap-2 items-baseline">
            <span className="text-2xl font-bold text-[#FF6C6C]">₵{parseFloat(bogoData.bogo_item_price || 0).toLocaleString()}</span>
            <span className="text-xs font-semibold text-orange-600">BOGO</span>
          </div>
          {bogoData.bogo_quantity && bogoData.bogo_free_quantity && (
            <p className="text-xs text-gray-600">
              Buy {bogoData.bogo_quantity} Get {bogoData.bogo_free_quantity} Free
            </p>
          )}
        </div>
      );
    }

    // Bundle Offer
    if (offer_type === "bundle" && bundle && bundle.length > 0 && bundle[0].bundle_price) {
      const bundleData = bundle[0];
      return (
        <div className="space-y-1">
          <div className="flex gap-2 items-baseline">
            <span className="text-2xl font-bold text-[#FF6C6C]">₵{parseFloat(bundleData.bundle_price || 0).toLocaleString()}</span>
            <span className="text-xs font-semibold text-purple-600">Bundle</span>
          </div>
          <div className="flex gap-2 items-center">
            <span className="text-sm text-gray-500 line-through">₵{parseFloat(bundleData.original_sum || 0).toLocaleString()}</span>
            <span className="text-xs font-semibold text-green-600">Save ₵{parseFloat(bundleData.bundle_savings || 0).toLocaleString()}</span>
          </div>
        </div>
      );
    }

    // Regular Discount Offer (offer_type is null or discount)
    const originalPriceValue = originalPrice || price || 0;
    const discountedPriceValue = discounted_price || originalPriceValue;
    
    return (
      <div className="space-y-1">
        <div className="flex gap-2 items-baseline">
          <span className="text-2xl font-bold text-[#FF6C6C]">₵{parseFloat(discountedPriceValue).toLocaleString()}</span>
          {discount_percentage && (
            <span className="text-xs font-semibold text-orange-600">{discount_percentage}% OFF</span>
          )}
        </div>
        {originalPriceValue !== discountedPriceValue && (
          <span className="text-sm text-red-500 line-through">₵{parseFloat(originalPriceValue).toLocaleString()}</span>
        )}
      </div>
    );
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, delay: index * 0.1 }}
      viewport={{ once: true }}
      className="group"
    >
      <div
        onClick={() => handleViewDeal(deal)}
        className={`relative flex flex-col h-full overflow-hidden bg-white rounded-xl transition-all duration-300 cursor-pointer hover:-translate-y-1 ${
          isFlashSale 
            ? "border-2 border-orange-500 shadow-xl shadow-orange-500/50 hover:shadow-2xl hover:shadow-orange-500/60" 
            : "border border-gray-200 hover:shadow-lg"
        }`}
      >
        {/* Flash Sale Glow Effect */}
        {isFlashSale && (
          <div className="absolute top-0 right-0 left-0 z-0 h-full bg-gradient-to-b to-transparent rounded-xl pointer-events-none from-orange-500/10" />
        )}
        
        {/* Image Section */}
        <div className="relative flex-shrink-0 bg-white">
          <img
            src={image}
            alt={title}
            className="object-cover w-full h-56"
          />
          
          {/* Flash Sale Banner - Top Priority */}
          {isFlashSale && (
            <div className="absolute top-0 right-0 left-0 z-10">
              <div className="flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-bold text-white bg-gradient-to-r from-orange-500 to-red-600 shadow-lg animate-pulse">
                <Zap className="w-3.5 h-3.5 fill-white animate-pulse" />
                Flash Sale!
              </div>
            </div>
          )}
          
          {/* Heart Icon */}
          <button className="absolute top-4 right-4 p-2 rounded-full transition-colors hover:bg-gray-100">
            <Heart className="w-5 h-5 text-gray-600" />
          </button>

          {/* Authenticated User Badge */}
          {isAuthenticated && (
            <div className={`absolute left-4 px-2 py-1 bg-gradient-to-r from-[#FF7F7F] to-pink-500 text-white text-xs font-semibold rounded-full shadow-lg ${isFlashSale ? 'top-12' : 'top-4'}`}>
              Affiliate Ready
            </div>
          )}

          {/* Offer Badge */}
          {(() => {
            if (offer_type === "bogo" && bogo && bogo.length > 0) {
              return (
                <div className="absolute bottom-4 left-4 px-2 py-1 text-xs font-semibold text-white bg-orange-500 rounded-full">
                  BOGO
                </div>
              );
            }
            if (offer_type === "bundle" && bundle && bundle.length > 0) {
              return (
                <div className="absolute bottom-4 left-4 px-2 py-1 text-xs font-semibold text-white bg-purple-600 rounded-full">
                  Bundle
                </div>
              );
            }
            if (discountPercentage > 0) {
              return (
                <div className="absolute bottom-4 left-4 px-2 py-1 text-xs font-semibold text-white bg-red-500 rounded-full">
                  -{discountPercentage}%
                </div>
              );
            }
            return null;
          })()}
        </div>

        {/* Content Section */}
        <div className="flex flex-col flex-1 p-4 space-y-1 md:space-y-2">
          {/* Deal Title and Sales Metric */}
          <div className="space-y-0.5">
            <h3 className="text-base font-bold leading-tight text-gray-900 truncate">{title}</h3>
            <span className="text-sm text-gray-500">3+ sold in the last 10 days</span>
          </div>

          {/* Seller Info */}
          <div className="flex justify-between items-center">
            <div className="flex gap-2 items-center">
              <span className="text-sm text-gray-600 truncate">{(company_name && company_name.length > 15) ? `${company_name.slice(0, 15)}...` : (company_name || "Jumia Ghana")}</span>
            </div>
          </div>

          {/* Flash Sale Countdown Timer */}
          {isFlashSale && countdown && !countdown.expired && (
            <div className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-bold text-white bg-gradient-to-r from-orange-500 to-red-600 rounded-md shadow-sm">
              <Clock className="w-3 h-3" />
              <span>
                {String(countdown.hours).padStart(2, '0')}:
                {String(countdown.minutes).padStart(2, '0')}:
                {String(countdown.seconds).padStart(2, '0')}
              </span>
            </div>
          )}

          {/* Flash Sale Expired */}
          {isFlashSale && isExpired && (
            <div className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-bold text-white bg-gradient-to-r from-red-500 to-red-700 rounded-md shadow-sm">
              <Clock className="w-3 h-3" />
              <span>Expired</span>
            </div>
          )}

          {/* Regular Deal Countdown Timer */}
          {!isFlashSale && daysRemaining !== null && daysRemaining > 0 && (
            <div className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-bold text-white bg-gradient-to-r from-green-500 to-lime-500 rounded-md shadow-sm">
              <Clock className="w-3 h-3" />
              <span>{daysRemaining} {daysRemaining === 1 ? 'day' : 'days'} left</span>
            </div>
          )}

          {/* Regular Deal Expired */}
          {!isFlashSale && daysRemaining !== null && daysRemaining <= 0 && (
            <div className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-bold text-white bg-gradient-to-r from-red-500 to-red-700 rounded-md shadow-sm">
              <Clock className="w-3 h-3" />
              <span>Expired</span>
            </div>
          )}

          {/* Pricing */}
          <div className="py-1">
            {renderPricing()}
          </div>

          {/* Apply Button */}
          <button
            onClick={(e) => {
              if (isExpired || (isAuthenticated && !isActive)) return;
              e.preventDefault();
              e.stopPropagation();
              handlePromote(deal);
            }}
            disabled={isExpired || (isAuthenticated && !isActive)}
            className={`px-4 py-1.5 w-full mt-auto font-medium rounded-lg border transition-colors duration-200 md:py-2.5 ${
              isExpired || (isAuthenticated && !isActive)
                ? "text-gray-500 bg-gray-100 border-gray-300 cursor-not-allowed"
                : "text-gray-900 bg-white border-orange-500 hover:bg-orange-50"
            }`}
            title={isAuthenticated && !isActive ? "Account must be activated to promote deals" : ""}
          >
            {isExpired 
              ? "Deal Expired" 
              : isAuthenticated && !isActive 
              ? "Activation Required" 
              : "Apply"}
          </button>
        </div>
      </div>
    </motion.div>
  );
};

export default ProductCard;
